/**
 * Brendon Videos Web Component
 *
 * Manages autoplay muted videos, mute/unmute toggles per card, and mobile carousel
 * with prev/next navigation. Pauses videos when section is out of view (IntersectionObserver).
 */
if (!customElements.get('brendon-videos')) {
  class BrendonVideos extends HTMLElement {
    /** @type {IntersectionObserver | null} */
    intersectionObserver = null;

    /** @type {ResizeObserver | null} */
    resizeObserver = null;

    /** @type {number} */
    carouselIndex = 0;

    /** @type {boolean} */
    prefersReducedMotion = false;

    /** @type {boolean} */
    carouselInitialized = false;

    /** @type {boolean} */
    isProgrammaticScroll = false;

    /** @type {number} */
    scrollSyncFrame = 0;

    /** @type {(() => void) | null} */
    onCarouselPrev = null;

    /** @type {(() => void) | null} */
    onCarouselNext = null;

    /** @type {((event: Event) => void) | null} */
    onViewportScroll = null;

    connectedCallback() {
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.playAllVideos();
      this.setupMuteButtons();
      this.setupCarousel();
      this.setupIntersectionObserver();
      if (this.prefersReducedMotion) {
        this.pauseAllVideos();
      }
    }

    disconnectedCallback() {
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.teardownCarousel();
      this.pauseAllVideos();
    }

    /**
     * Attempts to play all video elements inside the component.
     * @returns {void}
     */
    playAllVideos() {
      const videos = this.querySelectorAll('.brendon-in-action__video');
      for (const video of videos) {
        try {
          video.play();
        } catch (_) {
          // Autoplay may be blocked by browser policy
        }
      }
    }

    /**
     * Pauses all video elements inside the component.
     * @returns {void}
     */
    pauseAllVideos() {
      const videos = this.querySelectorAll('.brendon-in-action__video');
      for (const video of videos) {
        video.pause();
      }
    }

    /**
     * Binds mute/unmute toggle to each mute button and updates aria-label and icon visibility.
     * @returns {void}
     */
    setupMuteButtons() {
      const buttons = this.querySelectorAll('.brendon-in-action__mute-btn');
      for (const btn of buttons) {
        btn.addEventListener('click', this.onMuteClick.bind(this));
      }
    }

    /**
     * Handles mute button click: toggles video muted state and updates button UI.
     * @param {MouseEvent} e
     * @returns {void}
     */
    onMuteClick(e) {
      const btn = /** @type {HTMLButtonElement} */ (e.currentTarget);
      if (!btn) return;

      const card = btn.closest('.brendon-in-action__card');
      if (!card) return;

      const video = card.querySelector('.brendon-in-action__video');
      if (!(video instanceof HTMLVideoElement)) return;

      video.muted = !video.muted;
      const muted = video.muted;

      const mutedIcon = btn.querySelector('.brendon-in-action__mute-icon--muted');
      const unmutedIcon = btn.querySelector('.brendon-in-action__mute-icon--unmuted');

      if (mutedIcon) {
        mutedIcon.hidden = !muted;
      }
      if (unmutedIcon) {
        unmutedIcon.hidden = muted;
      }

      btn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');

      if (!video.muted) {
        try {
          video.play();
        } catch (_) {}
      }
    }

    /**
     * True when the mobile/tablet carousel is visible (desktop uses the grid).
     * @returns {boolean}
     */
    isCarouselActive() {
      const carousel = this.querySelector('.brendon-in-action__carousel');
      if (!carousel) return false;

      return getComputedStyle(carousel).display !== 'none';
    }

    /**
     * @returns {HTMLElement | null}
     */
    getCarouselViewport() {
      return /** @type {HTMLElement | null} */ (
        this.querySelector('.brendon-in-action__carousel-viewport')
      );
    }

    /**
     * @returns {HTMLElement | null}
     */
    getCarouselTrack() {
      return /** @type {HTMLElement | null} */ (
        this.querySelector('.brendon-in-action__carousel-track')
      );
    }

    /**
     * @returns {HTMLElement[]}
     */
    getCarouselCards() {
      const track = this.getCarouselTrack();
      if (!track) return [];

      return [...track.querySelectorAll('.brendon-in-action__card')];
    }

    /**
     * Sets up carousel prev/next buttons, native scroll sync, and resize updates.
     * @returns {void}
     */
    setupCarousel() {
      if (this.carouselInitialized) return;

      const viewport = this.getCarouselViewport();
      const track = this.getCarouselTrack();
      const prevBtn = this.querySelector('.brendon-in-action__carousel-prev');
      const nextBtn = this.querySelector('.brendon-in-action__carousel-next');

      if (!viewport || !track || !prevBtn || !nextBtn) return;

      const count = this.getCarouselCount();
      if (count <= 0) return;

      this.carouselInitialized = true;

      this.onCarouselPrev = () => {
        if (this.carouselIndex <= 0) return;

        this.setCarouselIndex(this.carouselIndex - 1);
      };
      this.onCarouselNext = () => {
        if (this.carouselIndex >= count - 1) return;

        this.setCarouselIndex(this.carouselIndex + 1);
      };

      prevBtn.addEventListener('click', this.onCarouselPrev);
      nextBtn.addEventListener('click', this.onCarouselNext);

      this.onViewportScroll = () => {
        if (this.isProgrammaticScroll) return;

        if (this.scrollSyncFrame) {
          cancelAnimationFrame(this.scrollSyncFrame);
        }

        this.scrollSyncFrame = requestAnimationFrame(() => {
          this.scrollSyncFrame = 0;
          this.syncIndexFromScroll();
        });
      };

      viewport.addEventListener('scroll', this.onViewportScroll, { passive: true });

      this.resizeObserver = new ResizeObserver(() => {
        if (!this.isCarouselActive()) return;

        this.scrollToIndex(this.carouselIndex, false);
      });
      this.resizeObserver.observe(viewport);

      requestAnimationFrame(() => {
        this.syncIndexFromScroll();
        this.updateCarouselNavState();
      });
    }

    /**
     * Removes carousel listeners when the element is disconnected.
     * @returns {void}
     */
    teardownCarousel() {
      if (!this.carouselInitialized) return;

      const viewport = this.getCarouselViewport();
      const prevBtn = this.querySelector('.brendon-in-action__carousel-prev');
      const nextBtn = this.querySelector('.brendon-in-action__carousel-next');

      if (prevBtn && this.onCarouselPrev) {
        prevBtn.removeEventListener('click', this.onCarouselPrev);
      }
      if (nextBtn && this.onCarouselNext) {
        nextBtn.removeEventListener('click', this.onCarouselNext);
      }
      if (viewport && this.onViewportScroll) {
        viewport.removeEventListener('scroll', this.onViewportScroll);
      }

      if (this.scrollSyncFrame) {
        cancelAnimationFrame(this.scrollSyncFrame);
        this.scrollSyncFrame = 0;
      }

      this.carouselInitialized = false;
      this.onCarouselPrev = null;
      this.onCarouselNext = null;
      this.onViewportScroll = null;
    }

    /**
     * Reads carousel count from CSS variable (set by Liquid).
     * @returns {number}
     */
    getCarouselCount() {
      const count = getComputedStyle(this).getPropertyValue('--brendon-in-action-carousel-count').trim();
      const num = Number.parseInt(count, 10);
      return Number.isNaN(num) ? 0 : num;
    }

    /**
     * Scroll offset for a slide index.
     * @param {number} index
     * @returns {number}
     */
    getScrollOffsetForIndex(index) {
      const cards = this.getCarouselCards();
      const card = cards[index];
      if (!card) return 0;

      return card.offsetLeft;
    }

    /**
     * Updates carousel index from the viewport scroll position.
     * @returns {void}
     */
    syncIndexFromScroll() {
      const viewport = this.getCarouselViewport();
      const cards = this.getCarouselCards();
      if (!viewport || cards.length === 0) return;

      const scrollLeft = viewport.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Infinity;

      for (let i = 0; i < cards.length; i++) {
        const distance = Math.abs(cards[i].offsetLeft - scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      }

      this.carouselIndex = closestIndex;
      this.updateCarouselNavState();
    }

    /**
     * Scrolls the viewport to a slide index.
     * @param {number} index
     * @param {boolean} smooth
     * @returns {void}
     */
    scrollToIndex(index, smooth = true) {
      const viewport = this.getCarouselViewport();
      if (!viewport || !this.isCarouselActive()) return;

      const offset = this.getScrollOffsetForIndex(index);
      const behavior = smooth && !this.prefersReducedMotion ? 'smooth' : 'auto';

      this.isProgrammaticScroll = true;
      viewport.scrollTo({ left: offset, behavior });

      window.setTimeout(() => {
        this.isProgrammaticScroll = false;
        this.syncIndexFromScroll();
      }, smooth && !this.prefersReducedMotion ? 350 : 0);
    }

    /**
     * Updates carousel index and scroll position.
     * @param {number} index
     * @returns {void}
     */
    setCarouselIndex(index) {
      const count = this.getCarouselCount();
      const clampedIndex = Math.max(0, Math.min(count - 1, index));

      this.carouselIndex = clampedIndex;
      this.updateCarouselNavState();
      this.scrollToIndex(clampedIndex);
    }

    /**
     * Enables/disables prev/next at the first and last slide (aria + class, not native disabled).
     * @returns {void}
     */
    updateCarouselNavState() {
      const count = this.getCarouselCount();
      const prevBtn = /** @type {HTMLButtonElement | null} */ (
        this.querySelector('.brendon-in-action__carousel-prev')
      );
      const nextBtn = /** @type {HTMLButtonElement | null} */ (
        this.querySelector('.brendon-in-action__carousel-next')
      );

      if (!prevBtn || !nextBtn) return;

      const atStart = this.carouselIndex <= 0;
      const atEnd = this.carouselIndex >= count - 1;

      prevBtn.classList.toggle('brendon-in-action__carousel-btn--disabled', atStart);
      nextBtn.classList.toggle('brendon-in-action__carousel-btn--disabled', atEnd);
      prevBtn.setAttribute('aria-disabled', String(atStart));
      nextBtn.setAttribute('aria-disabled', String(atEnd));
      prevBtn.tabIndex = atStart ? -1 : 0;
      nextBtn.tabIndex = atEnd ? -1 : 0;
    }

    /**
     * Sets up IntersectionObserver to pause videos when section is out of view.
     * @returns {void}
     */
    setupIntersectionObserver() {
      const videos = this.querySelectorAll('.brendon-in-action__video');
      if (videos.length === 0) return;

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const video = entry.target;
            if (!(video instanceof HTMLVideoElement)) continue;
            if (this.prefersReducedMotion) continue;

            if (entry.isIntersecting) {
              try {
                video.play();
              } catch (_) {}
            } else {
              video.pause();
            }
          }
        },
        { threshold: 0.25, rootMargin: '0px' },
      );

      for (const video of videos) {
        this.intersectionObserver.observe(video);
      }
    }
  }

  customElements.define('brendon-videos', BrendonVideos);
}
